import { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Icon,
  ScrollArea,
  Sheet,
  SheetPopup,
  SheetTitle,
  Text,
  TileIconSquare,
  useWidgetRegistry,
  type WidgetInstance,
} from "@salestrak/ui";
import type { ManagedWidget } from "#app/widgets/dashboards/managed-widgets";
import {
  useViewPaneAnchorInsets,
} from "#app/lib/hooks/use-anchor-insets";
import type { Omittable } from "@salestrak/ui";

export type ManageWidgetsPanelProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The fixed catalog of widgets this surface offers. */
  catalog: ManagedWidget[];
  /** The placed instances — a widget is "on" when an instance carries its `widgetKey`. */
  instances: WidgetInstance[];
  /** Toggle a catalog widget on (add at its designed slot) or off (remove it). */
  onToggle: (widget: ManagedWidget, enabled: boolean) => void;
}>;

/** The panel's 22rem sidebar width, as a class. */
const PANEL_POPUP = "w-88 max-w-none";

/** A subtle hover border cue on top of the stock card. Radius comes from the
 *  Card `radius` prop so the inner highlight ring stays in lockstep.
 *  DESIGN-TUNABLE. */
const CATALOG_TILE_CLASS =
  "cursor-pointer text-left transition-colors hover:border-(--border-strong)";

/** A disabled (out-of-scope) entry: no hover cue, no pointer, left-aligned copy. */
const DISABLED_TILE_CLASS = "cursor-default text-left";

/** The stable key an instance carries linking it back to its catalog entry. */
function instanceWidgetKey(instance: WidgetInstance): string | undefined {
  return (instance.config as { widgetKey?: string } | null)?.widgetKey ?? undefined;
}

/**
 * Manage widgets — a managed surface's ONLY authoring control. My Work and Team
 * Overview are curated sets (not a free add-any-widget gallery): this right
 * side panel lists that catalog as tiles with a gradient icon square top-left,
 * Added/Add chip (the 28px button rung) top-right, label, and description while the board stays
 * visible behind it. The board reflows under gesture-local GRAVITY around
 * every toggle: toggling on re-enters the widget at its designed slot (settled
 * through the grid so nothing overlaps); toggling off removes it and the
 * survivors under it rise to close that hole — and only that hole; space an
 * editor left on purpose elsewhere stays. A
 * `disabled` catalog entry is listed but not offered: greyed out, no toggle,
 * a "Coming soon" chip on the same rung where Add would be.
 *
 * Non-modal by construction (`modal={false}` + no backdrop): the old
 * `nonBlockingOverlay` prop bought exactly this, and Base UI leaves the page
 * scrollable and interactive for free.
 *
 * Pinned below the host's view tabs when it has them (Team Overview), so the
 * tab strip stays visible and the panel reads as part of the view, not as a
 * sheet over the whole screen; a tabless host (My Work) pins to the content
 * card instead — see `useViewPaneAnchorInsets`.
 */
export function ManageWidgetsPanel({
  open,
  onOpenChange,
  catalog,
  instances,
  onToggle,
}: ManageWidgetsPanelProps) {
  const registry = useWidgetRegistry();
  const anchorInsets = useViewPaneAnchorInsets(open);
  // Which catalog entries are currently placed, matched by the baked `widgetKey`.
  const enabledKeys = useMemo(
    () => new Set(instances.map(instanceWidgetKey).filter((k): k is string => !!k)),
    [instances],
  );
  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetPopup
        // Keep the sheet inside the view pane (below the tabs) or, failing
        // that, the app shell's content card. It must read as a panel in the
        // workspace, not as full-viewport page chrome — one hairline down its
        // open edge, flush with the region everywhere else (see
        // ANCHORED_SHEET_POPUP_CLASS).
        className={PANEL_POPUP}
        showBackdrop={false}
        // Anchored panel of the page, like the filter and Customize view
        // panels — it shares the page fill, not the floating popover surface.
        surface="background"
        anchorInsets={anchorInsets}
      >
        <Box
          direction="column"
          gap="sm"
          paddingX="md"
          paddingTop="sm"
          paddingBottom="sm"
        >
          <SheetTitle className="text-base">Widgets</SheetTitle>
          <Text size="xs" variant="muted" leading="normal">
            Click add or drag widgets below to your home screen. You can also reorder and
            remove them.
          </Text>
        </Box>

        <ScrollArea>
          <Box
            direction="column"
            gap="sm"
            paddingX="md"
            paddingTop="sm"
            paddingBottom="md"
          >
            {catalog.map((widget) => {
              const enabled = enabledKeys.has(widget.widgetKey);
              if (widget.disabled) {
                return (
                  <Card
                    key={widget.widgetKey}
                    className={DISABLED_TILE_CLASS}
                    radius="lg"
                    opacity={50}
                    aria-disabled
                  >
                    <Box direction="column" gap="sm" paddingX="md" paddingY="md">
                      <Box direction="row" align="center" justify="between">
                        <TileIconSquare
                          icon={registry.get(widget.type)?.icon ?? "square"}
                        />
                        {/* The same 28px chrome the Add chip wears, so every
                            tile's corner sits on one rung; the label alone
                            says it — no glyph, the tile is already dimmed. */}
                        <Button render={<Box as="span" />} size="sm" variant="outline">
                          Coming soon
                        </Button>
                      </Box>
                      <Box direction="column" gap="2xs">
                        <Text size="sm" weight="semibold" truncate>
                          {widget.label}
                        </Text>
                        <Text size="xs" variant="muted" truncate={2}>
                          {widget.description}
                        </Text>
                      </Box>
                    </Box>
                  </Card>
                );
              }
              return (
                <Card
                  key={widget.widgetKey}
                  render={<Box as="button" />}
                  className={CATALOG_TILE_CLASS}
                  radius="lg"
                  onClick={() => onToggle(widget, !enabled)}
                  aria-label={`${enabled ? "Hide" : "Show"} ${widget.label}`}
                >
                  <Box direction="column" gap="sm" paddingX="md" paddingY="md">
                    <Box direction="row" align="center" justify="between">
                      <Box pointerEvents="none">
                        <TileIconSquare
                          icon={registry.get(widget.type)?.icon ?? "square"}
                          color={enabled ? widget.color : undefined}
                        />
                      </Box>
                      {/* The whole tile is the press target, so the Add chip is
                          Button chrome on a span — the 28px `sm` rung, not an
                          18px count badge — and the state reads off the variant:
                          outline offers, the primary-hued outline (the
                          toolbar chip's selected rung) confirms. `secondary`
                          was tried: its fill sits at the dark card's own and
                          the chip vanished into bare text. */}
                      <Button
                        render={<Box as="span" />}
                        size="sm"
                        color={enabled ? "primary" : "neutral"}
                        variant="outline"
                      >
                        <Icon name={enabled ? "check" : "plus"} size="sm" />
                        {enabled ? "Added" : "Add"}
                      </Button>
                    </Box>
                    <Box direction="column" gap="2xs">
                      <Text size="sm" weight="semibold" truncate>
                        {widget.label}
                      </Text>
                      <Text size="xs" variant="muted" truncate={2}>
                        {widget.description}
                      </Text>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Box>
        </ScrollArea>
      </SheetPopup>
    </Sheet>
  );
}
